import { Alert, Skeleton } from '@mui/material';
import { useState } from 'react';

import { replaceUserGrants } from '../../api/business/endpoints';
import type { Grant, TenantUser } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { PermissionMatrix } from '../../components/PermissionMatrix';
import { useT } from '../../i18n/LanguageContext';

import { useGrantContext } from './useGrantContext';

/** Replaces a sub-user's grants. The change applies on their very next request (AC-06.2). */
export const EditPermissionsDialog = ({ user, onClose }: { user: TenantUser; onClose: () => void }) => {
  const t = useT();
  const context = useGrantContext();
  const [grants, setGrants] = useState<Array<Grant>>(user.grants);
  // Grants on archived companies are not in the matrix, but they are in `grants` and are saved back
  // untouched. Saying so stops an owner from concluding the user holds nothing there.
  const hidden: number = grants.filter(
    (grant) =>
      grant.companyId !== null && !context.companies.some((company) => company.id === grant.companyId),
  ).length;
  const mutation = useAppMutation(
    (next: Array<Grant>) => replaceUserGrants(user.id, next),
    ['users'],
    onClose,
  );

  return (
    <FormDialog
      title={t('users.permissionsTitle', { user: user.displayName ?? user.email })}
      onClose={onClose}
      onSubmit={() => mutation.mutate(grants)}
      isBusy={mutation.isPending}
      error={mutation.error ?? context.error}
      maxWidth="md"
    >
      {context.isLoading ? (
        <Skeleton variant="rounded" height={160} />
      ) : (
        <>
          {hidden > 0 ? (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              {t('users.hiddenGrants', { count: hidden })}
            </Alert>
          ) : null}
          <PermissionMatrix
            definitions={context.definitions}
            companies={context.companies}
            value={grants}
            onChange={setGrants}
            canGrant={context.canGrant}
          />
        </>
      )}
    </FormDialog>
  );
};
